/**
 * Full-screen import progress dialog shared across dashboard and credit report.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { ProcessingLoader } from '../credit-report/ProcessingLoader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { classifyImportError } from '../../utils/creditImportStatus';

const ProviderRefreshImportDialog = ({
  isOpen = false,
  importId = null,
  importSession = null,
  getAuthHeaders = null,
  onComplete,
  onClose,
  onRetry,
  onError = null,
}) => {
  const [importError, setImportError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setImportError(null);
    }
  }, [isOpen, importId]);

  const handleDismiss = useCallback(() => {
    setImportError(null);
    onClose?.();
  }, [onClose]);

  const handleComplete = useCallback((result) => {
    setImportError(null);
    onComplete?.(result);
  }, [onComplete]);

  const handleError = useCallback((error) => {
    if (onError?.(error) === 'handled') {
      return;
    }
    setImportError(classifyImportError(error));
  }, [onError]);

  const handleTryAgain = useCallback(() => {
    setImportError(null);
    onRetry?.();
  }, [onRetry]);

  if (!isOpen || !importId) {
    return null;
  }

  if (importError) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleDismiss();
        }}
        modal
      >
        <DialogContent
          className="sm:max-w-[480px]"
          aria-describedby="provider-import-error-message"
        >
          <DialogHeader>
            <DialogTitle>{importError.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <p
              id="provider-import-error-message"
              className="text-sm text-muted-foreground mb-6"
              data-testid="provider-import-error-message"
            >
              {importError.message}
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleDismiss}
                className="flex-1"
                data-testid="provider-import-error-close"
              >
                Close
              </Button>
              {importError.showTryAgain ? (
                <Button
                  type="button"
                  onClick={handleTryAgain}
                  className="flex-1"
                  data-testid="provider-import-error-retry"
                >
                  Try Again
                </Button>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleDismiss();
      }}
      modal
    >
      <DialogContent
        priority
        className="sm:max-w-[900px] w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto p-0 border-0 bg-transparent shadow-none"
        hideClose
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Importing Credit Report</DialogTitle>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-50 h-9 w-9 rounded-full bg-black/40 hover:bg-black/55 text-white"
          aria-label="Cancel import"
          data-testid="provider-import-dismiss-btn"
        >
          <X size={18} />
        </Button>
        <ProcessingLoader
          key={importId}
          isProcessing={Boolean(isOpen && importId && !importError)}
          importId={importId}
          importSession={importSession}
          getAuthHeaders={getAuthHeaders}
          onComplete={handleComplete}
          onError={handleError}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ProviderRefreshImportDialog;
